import { Link } from '@tanstack/react-router'
import type { InferResponseType } from 'hono/client'
import {
  Download,
  PencilIcon,
  ReplaceIcon,
  TrashIcon,
  ViewIcon,
} from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { apiClient } from '@/apiClient'

// Type for the single document response from the API
type DocumentWithDetails = InferResponseType<
  (typeof apiClient.documents)[':documentId']['$get']
>['data']

interface DocumentViewSearch {
  mode?: 'view' | 'modify'
  pageNum?: number
  previewMode?: 'image' | 'markdown'
  tab?: 'details' | 'preview' | 'attachments' | 'logs'
}

interface DocumentActionsToolbarProps {
  documentId: string
  search: DocumentViewSearch
  mode: 'view' | 'modify'
  document: DocumentWithDetails | undefined
  onChangeStatus: () => void
  onDownload: () => void
  onDelete: () => void
}

export function DocumentActionsToolbar({
  documentId,
  search,
  mode,
  document,
  onChangeStatus,
  onDownload,
  onDelete,
}: DocumentActionsToolbarProps) {
  return (
    <div className="flex gap-1">
      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Link
              to="/documents/$documentId"
              params={{ documentId }}
              search={{
                ...search,
                mode: mode === 'view' ? 'modify' : 'view',
              }}
              replace
            >
              <Button variant="link" size="icon">
                {mode === 'view' ? <PencilIcon /> : <ViewIcon />}
              </Button>
            </Link>
          </TooltipTrigger>
          <TooltipContent>
            {mode === 'view'
              ? 'Passa alla modalitá di modifica'
              : 'Passa alla modalitá di visualizzazione'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="link" size="icon" onClick={onChangeStatus}>
              <ReplaceIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {document?.status === 'in_review'
              ? 'Cambia stato in "Pronto"'
              : 'Cambia stato in "In review"'}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {document?.file_id && (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="link" size="icon" onClick={onDownload}>
                <Download />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Scarica documento</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}

      <TooltipProvider delayDuration={0}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="link" size="icon" onClick={onDelete}>
              <TrashIcon />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Elimina</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  )
}
